<template>
  <div>
    <div v-if="balanceInCredit" data-testid="spayg-repayment-credit-info">
      <p>
        Although the account is in credit we still need to set up automatic
        repayment coverage. This will help to recover any outstanding balance on
        your account after you receive your last bill from us after your switch
        to Smart PAYG.
      </p>
      <br />
      <p>
        The repayment won’t come as a separate bill or Direct Debit, instead, a
        small amount will be automatically taken from your energy credit from
        the meter each night.
      </p>
    </div>
    <div v-else data-testid="spayg-repayment-debt-info">
      <p>
        We need to set up a repayment amount for your meter. This will help to
        recover any outstanding balance on your account after you receive your
        last bill from us after your switch to Smart PAYG.
      </p>
      <br />
      <p>
        The repayment won’t come as a separate bill or Direct Debit, instead, a
        small amount will be automatically taken from your energy credit from
        the meter each night.
      </p>
    </div>

    <div class="w-50">
      <dl class="mt-5 mb-7 dl font-weight-medium">
        <dt>Current Balance:</dt>
        <dd>
          <span
            data-testid="spayg-repayment-balance"
            :class="!balanceInCredit && 'text-red'"
          >
            {{
              formatMoney({ amount: currentBalance, showNegativeSign: false })
            }}</span
          >
          <span>{{ balanceInCredit ? " in credit" : " in debt" }}</span>
        </dd>
        <dt>Monthly total:</dt>
        <dd data-testid="spayg-repayment-monthly-total">
          {{
            formatMoney({
              amount: Number(monthlyTotal),
              showNegativeSign: false,
            })
          }}
        </dd>
        <dt v-if="!balanceInCredit">Debt cleared:</dt>
        <dd v-if="!balanceInCredit" data-testid="spayg-repayment-cleared-date">
          {{ payOffDate }} ({{ daysUntilPayoffDate }} days)
        </dd>
      </dl>
    </div>

    <div class="w-50">
      <SoNumberField
        v-model="repaymentAmount"
        data-testid="spayg-repayment-amount"
        label="Repayment amount £"
        name="repaymentAmount"
        class="mb-1"
        :step="0.1"
        :rules="[
          rules.required,
          (val) =>
            Number(val) <= repaymentRateDefaultValues?.max ||
            'The amount must be no more than £' +
              repaymentRateDefaultValues?.max,
          (val) =>
            Number(val) >= repaymentRateDefaultValues?.min ||
            'The amount must be at least £' + repaymentRateDefaultValues?.min,
        ]"
      />

      <span
        v-if="balanceInCredit"
        data-testid="spayg-repayment-recommended"
        class="text-grey"
      >
        We recommend £{{ repaymentRateDefaultValues?.default }} daily for
        accounts currently in credit.
      </span>
    </div>
    <SoAlert
      v-if="balanceInCredit"
      data-testid="spayg-repayment-warning"
      class="mt-10 mb-5"
      type="warning"
      title="Final bill may create debt"
      >While your account shows a credit balance now, your final bill may exceed
      this amount, potentially creating debt on your account. Daily payments
      will automatically start only if that happens.</SoAlert
    >
  </div>
</template>
<script lang="ts">
  import { defineComponent } from "vue"
  import rules from "@/validators/rules"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import dayjs from "dayjs"
  import SoAlert from "@/components/SoAlert.vue"
  import { formatMoney } from "@/helpers/moneyHelpers"

  import SoNumberField from "@/components/formControls/SoNumberField.vue"
  export default defineComponent({
    components: {
      SoNumberField,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Number,
      },
      repaymentRateDefaultValues: {
        type: Object,
        default: () => ({}),
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        rules,
        formatMoney,
      }
    },
    computed: {
      repaymentAmount: {
        get(): number {
          return this.modelValue || 0
        },
        set(val: number[]) {
          this.$emit("update:modelValue", val)
        },
      },
      currentBalance() {
        return this.current?.account?.data?.balance ?? 0
      },
      balanceInCredit() {
        return this.currentBalance <= 0
      },
      monthlyTotal() {
        return (Number(this.repaymentAmount) * 31).toFixed(2)
      },
      daysUntilPayoffDate() {
        return Math.abs(
          Number(this.current.account.data.balance) /
            Number(this.repaymentAmount),
        ).toFixed()
      },
      payOffDate() {
        const today = dayjs()
        const payOffDate = today.add(Number(this.daysUntilPayoffDate), "day")
        return payOffDate.format("DD/MM/YYYY")
      },
    },
    mounted() {
      this.repaymentAmount = this.repaymentRateDefaultValues?.default || 0
    },
  })
</script>
